/** LRC timed lyrics — same spirit as FreeMusiers player parseLRC. */

export type LrcLine = {
  time: number;
  text: string;
};

export type LrcMeta = {
  ti?: string;
  ar?: string;
  al?: string;
  /** Offset from [offset:±ms] tag, milliseconds. */
  tagOffsetMs: number;
};

export type LrcDocument = {
  lines: LrcLine[];
  meta: LrcMeta;
};

const TIME_RE = /\[(\d{1,2}):(\d{2})(?:[.:](\d{1,3}))?\]/g;
const META_RE = /^\[(ti|ar|al|offset|by|re|ve|length):(.*)\]$/i;

function fracToSeconds(frac: string | undefined): number {
  if (!frac) return 0;
  if (frac.length === 3) return parseInt(frac, 10) / 1000;
  if (frac.length === 1) return parseInt(frac, 10) / 10;
  return parseInt(frac, 10) / 100;
}

/** Parse LRC / enhanced-ish LRC into sorted timed lines. */
export function parseLrc(lrcText: string): LrcDocument | null {
  if (!lrcText || typeof lrcText !== 'string') return null;
  const lines: LrcLine[] = [];
  const meta: LrcMeta = { tagOffsetMs: 0 };

  for (const raw of lrcText.split(/\r?\n/)) {
    const line = raw.trim();
    if (!line) continue;

    const metaMatch = line.match(META_RE);
    if (metaMatch) {
      const key = metaMatch[1].toLowerCase();
      const val = metaMatch[2].trim();
      if (key === 'ti') meta.ti = val;
      else if (key === 'ar') meta.ar = val;
      else if (key === 'al') meta.al = val;
      else if (key === 'offset') {
        const n = parseInt(val, 10);
        if (Number.isFinite(n)) meta.tagOffsetMs = n;
      }
      continue;
    }

    TIME_RE.lastIndex = 0;

    const stamps: { time: number; end: number }[] = [];
    let m: RegExpExecArray | null;
    while ((m = TIME_RE.exec(line)) !== null) {
      const minutes = parseInt(m[1], 10);
      const seconds = parseInt(m[2], 10);
      const t = minutes * 60 + seconds + fracToSeconds(m[3]);
      stamps.push({ time: t, end: m.index + m[0].length });
    }
    if (!stamps.length) continue;

    const text = line.slice(stamps[stamps.length - 1].end).trim();
    // Keep empty lines as breath pauses only if they have timestamps? skip empties
    if (!text) continue;
    for (const s of stamps) {
      lines.push({ time: s.time, text });
    }
  }

  lines.sort((a, b) => a.time - b.time || a.text.localeCompare(b.text));
  // de-dupe exact time+text
  const deduped: LrcLine[] = [];
  for (const L of lines) {
    const prev = deduped[deduped.length - 1];
    if (prev && prev.time === L.time && prev.text === L.text) continue;
    deduped.push(L);
  }

  if (!deduped.length) return null;
  return { lines: deduped, meta };
}

/** Untimed plain lyrics → fake lines (time < 0) for static display. */
export function plainToDoc(plain: string): LrcDocument | null {
  const parts = plain
    .split(/\r?\n/)
    .map((s) => s.trim())
    .filter(Boolean);
  if (!parts.length) return null;
  return {
    lines: parts.map((text, i) => ({ time: -1 - i, text })),
    meta: { tagOffsetMs: 0 },
  };
}

export function isTimed(doc: LrcDocument | null | undefined): boolean {
  return !!doc && doc.lines.length > 0 && doc.lines[0].time >= 0;
}

/**
 * Index of the active line at audio time (seconds).
 * Returns -1 before first line.
 */
export function activeIndex(
  lines: LrcLine[],
  timeSec: number,
  offsetSec = 0,
): number {
  if (!lines.length || lines[0].time < 0) return -1;
  const t = timeSec + offsetSec;
  let lo = 0;
  let hi = lines.length - 1;
  let ans = -1;
  while (lo <= hi) {
    const mid = (lo + hi) >> 1;
    if (lines[mid].time <= t) {
      ans = mid;
      lo = mid + 1;
    } else {
      hi = mid - 1;
    }
  }
  return ans;
}

/** Progress 0..1 through current line until next (for fades). */
export function lineProgress(
  lines: LrcLine[],
  idx: number,
  timeSec: number,
  offsetSec = 0,
): number {
  if (idx < 0 || idx >= lines.length || lines[0].time < 0) return 0;
  const t = timeSec + offsetSec;
  const start = lines[idx].time;
  const end = idx + 1 < lines.length ? lines[idx + 1].time : start + 4;
  const span = Math.max(0.05, end - start);
  return Math.min(1, Math.max(0, (t - start) / span));
}

export function formatLrcSummary(doc: LrcDocument | null): string {
  if (!doc) return 'no lyrics';
  if (!isTimed(doc)) return `${doc.lines.length} plain lines (untimed)`;
  const last = doc.lines[doc.lines.length - 1]?.time ?? 0;
  const mm = Math.floor(last / 60);
  const ss = Math.floor(last % 60)
    .toString()
    .padStart(2, '0');
  return `${doc.lines.length} LRC lines · to ${mm}:${ss}`;
}
